import { clsx } from "clsx";

import { useControlledState } from "@react-stately/utils";
import { LazyMotion, domAnimation, m } from "framer-motion";
import React from "react";

export type Step = {
  ["aria-label"]?: string;
  /**
   * For displaying label next to step
   */
  label?: string;
};

export type StepperProps = {
  className?: string;
  currentStep?: number;
  defaultStep?: number;
  navAriaLabel?: string;
  onStepChange?: (stepIndex: number) => void;
  stepClassName?: string;
  steps: Step[];
};

export const Stepper = React.forwardRef<HTMLButtonElement, StepperProps>(
  (
    {
      navAriaLabel,
      className,
      currentStep: currentStepProp,
      defaultStep = 0,
      onStepChange,
      stepClassName,
      steps = [],
    },
    ref,
  ) => {
    const [currentStep, setCurrentStep] = useControlledState(
      currentStepProp,
      defaultStep,
      onStepChange,
    );

    const stepCircleClasses = {
      inactive: "bg-gray-200 border-gray-200 text-black",
      active: "bg-transparent border-primary text-primary",
      complete: "bg-primary border-primary text-white",
    };

    const labelClasses = {
      inactive: "text-gray-600",
      active: "text-black",
      complete: "text-black",
    };

    const lineClasses = {
      default: "bg-gray-300",
      complete: "bg-primary",
    };

    return (
      <LazyMotion features={domAnimation}>
        <nav
          aria-label={navAriaLabel}
          className="-my-4 max-w-fit overflow-x-auto px-2  py-4"
          ref={ref}
        >
          <div
            role="progressbar"
            aria-valuenow={currentStep + 1}
            aria-valuemin={1}
            aria-valuemax={steps.length}
            aria-label={`Step ${currentStep + 1} of ${steps.length}`}
            aria-live="polite"
          >
            <ol
              className={clsx("flex flex-row flex-nowrap gap-x-3", className)}
            >
              {steps?.map((step, stepIdx) => {
                const status =
                  currentStep === stepIdx
                    ? "active"
                    : currentStep < stepIdx
                    ? "inactive"
                    : "complete";

                return (
                  <li
                    key={stepIdx}
                    aria-label={step["aria-label"] || `Step ${stepIdx}`}
                    className="relative flex w-full items-center pr-12"
                  >
                    <button
                      aria-current={status === "active" ? "step" : undefined}
                      onClick={() => setCurrentStep(stepIdx)}
                      className={clsx(
                        "group flex w-full cursor-pointer flex-row items-center justify-center gap-x-3 rounded-large py-2.5",
                        "!outline-none-v3 focus-visible:!outline-focus-visible focus-visible:!outline-offset-4",
                        stepClassName,
                      )}
                    >
                      <div className="relative flex items-center">
                        <m.div animate={status} className="relative">
                          <m.div
                            className={clsx(
                              "relative flex h-[30px] w-[30px] items-center justify-center rounded-full border-medium text-body-md font-semibold text-default-foreground",
                              stepCircleClasses[status],
                            )}
                            initial={false}
                            transition={{ duration: 0.25 }}
                          >
                            <div
                              className={clsx(
                                "flex items-center justify-center",
                                stepCircleClasses[status],
                                status === "active"
                                  ? "font-bold"
                                  : "font-medium",
                              )}
                            >
                              <span>{stepIdx + 1}</span>
                            </div>
                          </m.div>
                        </m.div>
                      </div>
                      {step.label && (
                        <div className="max-w-full flex-1 text-start">
                          <div
                            className={clsx(
                              "lg:text-medium text-small transition-[color,opacity] duration-300 group-active:opacity-80",
                              labelClasses[status],
                              status === "active" ? "font-bold" : "font-medium",
                            )}
                          >
                            {step.label}
                          </div>
                        </div>
                      )}
                      {stepIdx < steps.length - 1 && (
                        <div
                          aria-hidden="true"
                          className="pointer-events-none absolute right-0 w-10 flex-none items-center"
                        >
                          <div
                            className={clsx(
                              "relative h-0.5 w-full transition-colors duration-300",
                              currentStep > stepIdx
                                ? lineClasses.complete
                                : lineClasses.default,
                            )}
                          />
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </nav>
      </LazyMotion>
    );
  },
);

Stepper.displayName = "RecareUI.Stepper";
